import { useCallback, useMemo, useState } from 'react';
import {
  useGetLaunchedCampaignsQuery,
  useUpdateLaunchedCampaignMutation,
} from '../endpoints/launched-campaigns.endpoints';
import type { CampaignStatusFilter, LaunchedCampaign } from '../types/campaigns.types';
import type { CampaignRequestAction } from '../utils/campaign-request-actions.utils';
import { mapLaunchedCampaign, statusFilterToQuery } from '../utils/campaigns.mappers';

export function useLaunchedCampaigns() {
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('all');
  const [page, setPage] = useState(1);

  const queryParams = useMemo(() => ({
    page,
    page_size: 25,
    status: statusFilterToQuery(statusFilter),
  }), [page, statusFilter]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetLaunchedCampaignsQuery(queryParams);

  const [updateCampaign, { isLoading: isUpdating }] = useUpdateLaunchedCampaignMutation();

  const campaigns = useMemo<LaunchedCampaign[]>(
    () => (data?.items ?? []).map(mapLaunchedCampaign),
    [data?.items],
  );

  const handleStatusFilterChange = useCallback((value: CampaignStatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const runAction = useCallback(async(campaignId: string, action: CampaignRequestAction) => {
    await updateCampaign({
      campaignId,
      action,
    }).unwrap();
  }, [updateCampaign]);

  const assignClient = useCallback(async(campaignId: string, clientId: string | null) => {
    await updateCampaign({
      campaignId,
      clientId,
    }).unwrap();
  }, [updateCampaign]);

  return {
    campaigns,
    totalCount: data?.total_objects ?? 0,
    page,
    pageSize: 25,
    setPage,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    runAction,
    assignClient,
    isLoading: isLoading || isFetching,
    isError,
    isUpdating,
    refetch,
  };
}
